import classNames from 'classnames';
import Icon, { IconProps } from '../../Atoms/Icon';
import Title from '../../Atoms/Title';
import svg from '../../../constants/svg';

export interface Props {
  title: string;
  href?: string;
  className?: string;
  bgImage?: string;
  icon?: IconProps;
  badge?: string;
  onClick?: () => void;
}

interface ListProps {
  cards: Props[];
  elemInRow?: 3 | 4 | 'auto';
  className?: string;
}

function CardWorkspace({
  href = '#!',
  title,
  className,
  bgImage,
  icon,
  badge,
  onClick
}: Props) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={classNames([
        'group relative flex w-full flex-row items-center gap-4 overflow-hidden rounded-xl bg-white p-5 sm:h-80 sm:flex-col sm:items-stretch sm:justify-end sm:gap-0 sm:p-0 md:max-w-[280px]',
        className
      ])}
    >
      <span
        className="pointer-events-none absolute inset-0 z-20 opacity-0 transition duration-500 group-hover:opacity-100 sm:group-hover:opacity-90"
        style={{
          backgroundImage:
            'linear-gradient(225deg,#606AF8 0%,#5F6FFF 100%),linear-gradient(225deg,rgba(110,119,248,0.9) 0%,rgba(96,106,248,0.9) 100%)'
        }}
      />
      {bgImage && (
        <span
          className={classNames([
            'absolute inset-0 z-10 hidden h-full bg-cover bg-center transition duration-300 sm:block'
          ])}
          style={{
            backgroundImage: `url(${bgImage})`
          }}
        />
      )}
      {icon && (
        <span className="relative z-50 flex h-12 w-12 min-w-[48px] items-center justify-center rounded-xl bg-sl-indigo text-white duration-500 ease-out group-hover:bg-white group-hover:text-sl-indigo group-hover:ease-in  sm:mt-auto sm:ml-5 lg:ml-7">
          <Icon icon={icon} className="h-6 w-6" />
        </span>
      )}
      <span
        className={classNames([
          'relative z-40 flex flex-col items-start justify-end gap-1 transition duration-700 before:absolute before:left-0 before:bottom-0 before:z-0 before:hidden before:h-full before:w-full before:bg-white before:duration-500 before:ease-in-out group-hover:text-white group-hover:before:-bottom-full group-hover:before:ease-out sm:min-h-[85px] sm:gap-2 sm:p-5 sm:before:block lg:min-h-[116px] lg:p-6',
          badge && 'sm:py-4 lg:py-5'
        ])}
      >
        <Title
          as="h2"
          size={5}
          className="relative z-10 text-sl-blue duration-700 group-hover:text-white"
        >
          {title}
        </Title>
        {badge && (
          <span className="relative z-10 text-sm text-sl-indigo duration-700 group-hover:text-white lg:text-lg">
            {badge}
          </span>
        )}
        <span
          className="pointer-events-none absolute bottom-full left-0 hidden h-16 w-full bg-cover bg-center bg-no-repeat duration-500 ease-in-out group-hover:-bottom-full group-hover:ease-out sm:block"
          style={{
            backgroundImage: `url(${svg.cardDecor})`
          }}
        />
      </span>
    </a>
  );
}

export function CardWorkspaceList({
  cards,
  elemInRow = 3,
  className
}: ListProps) {
  return (
    <span
      className={classNames([
        'mx-auto flex flex-wrap justify-center py-3',
        elemInRow === 3 && 'max-w-4xl',
        elemInRow === 4 && 'lg:max-w-4xl xl:max-w-7xl',
        elemInRow === 'auto' && 'max-w-full',
        className
      ])}
    >
      {cards.map((card) => (
        <div
          key={cards.indexOf(card)}
          className={classNames([
            'p-3',
            elemInRow === 3 && 'w-full sm:w-1/2 md:w-1/3',
            elemInRow === 4 &&
              'w-full sm:w-1/2 sm:max-w-[304px] md:w-1/3 xl:w-1/4',
            elemInRow === 'auto' &&
              'w-full sm:max-w-[304px]'
          ])}
        >
          <CardWorkspace {...card} />
        </div>
      ))}
    </span>
  );
}

export default CardWorkspace;
